import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { applyWorshipDraft } from '@/components/programs/apply-worship-draft';
import {
  ensureProgramOccurrenceId,
  findOccurrenceForProgramDate,
} from '@/components/programs/ensure-occurrence';
import { buildWorshipMemberOptions } from '@/components/programs/worship-member-options';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormField } from '@/components/shared/form-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

const ROLE_KINDS = [
  'liturgist',
  'service_leader',
  'bible_reader',
  'communion',
  'other',
] as const;

const ALL_ROLE_KINDS = [
  'preacher',
  'liturgist',
  'service_leader',
  'bible_reader',
  'communion',
  'other',
] as const;

type RoleKind = (typeof ALL_ROLE_KINDS)[number];

interface WorshipAssignment {
  id: string;
  memberId: string;
  memberName: string;
  role: string;
  roleKind: RoleKind | null;
  status: string;
}

interface DraftAssignment {
  id: string;
  memberId: string;
  memberName: string;
  roleKind: RoleKind;
}

interface WorshipPayload {
  occurrenceId: string;
  preacherMemberId: string | null;
  preacherMemberName: string | null;
  includesCommunion: boolean;
  assignments: WorshipAssignment[];
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface MinisterOption {
  memberId: string;
  memberName: string;
  title: string;
  role: string;
  isActive: boolean;
}

export interface WorshipPlanningHandle {
  applyDraft: () => Promise<void>;
}

export interface WorshipPlanningSectionProps {
  programId: string;
  /** Dates from the day details record — worship applies to every one of these. */
  dates: string[];
  /** Hold worship changes locally until `applyDraft` is called. */
  deferSave?: boolean;
  /** Load existing worship when editing day details (read-only until save). */
  hydrateExisting?: boolean;
  /** Compact layout inside the day-details dialog. */
  embedded?: boolean;
  /** Right-hand column inside the day-details dialog. */
  sidePanel?: boolean;
  className?: string;
}

export const WorshipPlanningSection = forwardRef<
  WorshipPlanningHandle,
  WorshipPlanningSectionProps
>(function WorshipPlanningSection(
  {
    programId,
    dates,
    deferSave = false,
    hydrateExisting = false,
    embedded = false,
    sidePanel = false,
    className,
  },
  ref,
) {
  const { t } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canEdit = hasPermission(membership, PERMISSIONS.PROGRAMS_MANAGE_WORSHIP);

  const planDates = useMemo(
    () => [...new Set(dates)].sort((a, b) => a.localeCompare(b)),
    [dates],
  );

  const [occurrenceId, setOccurrenceId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [roleKind, setRoleKind] = useState<RoleKind>('liturgist');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberSelected, setMemberSelected] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [preacherQuery, setPreacherQuery] = useState('');
  const [preacherSelected, setPreacherSelected] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const [draftPreacherId, setDraftPreacherId] = useState<string | null>(null);
  const [draftIncludesCommunion, setDraftIncludesCommunion] = useState(false);
  const [draftAssignments, setDraftAssignments] = useState<DraftAssignment[]>([]);
  const [draftHydrated, setDraftHydrated] = useState(false);

  const primaryPlanDate = planDates[0] ?? null;

  const worshipApplyPayload = useMemo(
    () => (planDates.length > 1 ? { applyToOccurrenceDates: planDates } : {}),
    [planDates],
  );

  useEffect(() => {
    if (deferSave) {
      setOccurrenceId(null);
      return;
    }

    let cancelled = false;
    if (!programId || !primaryPlanDate) {
      setOccurrenceId(null);
      return;
    }

    setResolving(true);
    void ensureProgramOccurrenceId({ programId, occurrenceDate: primaryPlanDate })
      .then((id) => {
        if (!cancelled) setOccurrenceId(id);
      })
      .catch((error) => {
        if (!cancelled) {
          setOccurrenceId(null);
          toast.error(resolveApiError(error, tErrors));
        }
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [deferSave, programId, primaryPlanDate, tErrors]);

  useEffect(() => {
    if (!deferSave) return;

    setDraftPreacherId(null);
    setDraftIncludesCommunion(false);
    setDraftAssignments([]);
    setPreacherSelected(null);
    setDraftHydrated(!hydrateExisting);
  }, [deferSave, hydrateExisting, programId, primaryPlanDate]);

  useEffect(() => {
    if (!deferSave || !hydrateExisting || draftHydrated || !programId || !primaryPlanDate) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const existingOccurrenceId = await findOccurrenceForProgramDate({
          programId,
          occurrenceDate: primaryPlanDate,
        });
        if (cancelled) return;

        if (!existingOccurrenceId) {
          setDraftHydrated(true);
          return;
        }

        const worshipData = await api.get<WorshipPayload>(
          `/programs/occurrences/${existingOccurrenceId}/worship`,
        );
        if (cancelled) return;

        setDraftPreacherId(worshipData.preacherMemberId);
        setDraftIncludesCommunion(worshipData.includesCommunion);
        setDraftAssignments(
          worshipData.assignments
            .filter((assignment) => assignment.roleKind !== 'preacher')
            .map((assignment) => ({
              id: assignment.id,
              memberId: assignment.memberId,
              memberName: assignment.memberName,
              roleKind: assignment.roleKind ?? 'other',
            })),
        );
        if (worshipData.preacherMemberId && worshipData.preacherMemberName) {
          setPreacherSelected({
            id: worshipData.preacherMemberId,
            label: worshipData.preacherMemberName,
          });
        }
      } catch (error) {
        if (!cancelled) toast.error(resolveApiError(error, tErrors));
      } finally {
        if (!cancelled) setDraftHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    deferSave,
    draftHydrated,
    hydrateExisting,
    primaryPlanDate,
    programId,
    tErrors,
  ]);

  const worship = useQuery({
    queryKey: ['programs', 'occurrences', occurrenceId, 'worship'],
    queryFn: () =>
      api.get<WorshipPayload>(`/programs/occurrences/${occurrenceId}/worship`),
    enabled: !deferSave && Boolean(occurrenceId),
  });

  const ministers = useQuery({
    queryKey: ['people', 'ministers', 'picker', 'worship'],
    enabled: canEdit,
    queryFn: () => api.get<{ data: MinisterOption[] }>('/people/ministers?pageSize=100'),
  });

  const activeMemberQuery = assignOpen ? memberQuery : preacherQuery;

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'worship', activeMemberQuery],
    enabled: canEdit && (deferSave || assignOpen || Boolean(occurrenceId)),
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '20' });
      if (activeMemberQuery.trim()) params.set('q', activeMemberQuery.trim());
      return api.get<{ data: MemberOption[] }>(`/people/members?${params.toString()}`);
    },
  });

  const ministerRows = ministers.data?.data ?? [];
  const memberRows = members.data?.data ?? [];

  const preacherOptions = useMemo(
    () => buildWorshipMemberOptions(ministerRows, memberRows, preacherQuery),
    [ministerRows, memberRows, preacherQuery],
  );

  const assignMemberOptions = useMemo(
    () => buildWorshipMemberOptions(ministerRows, memberRows, memberQuery),
    [ministerRows, memberRows, memberQuery],
  );

  const visibleAssignments = useMemo(() => {
    if (deferSave) return draftAssignments;
    return (worship.data?.assignments ?? []).filter(
      (assignment) => assignment.roleKind !== 'preacher',
    );
  }, [deferSave, draftAssignments, worship.data?.assignments]);

  const preacherMemberId = deferSave
    ? draftPreacherId
    : (worship.data?.preacherMemberId ?? null);
  const includesCommunion = deferSave
    ? draftIncludesCommunion
    : (worship.data?.includesCommunion ?? false);

  useEffect(() => {
    if (deferSave || !worship.data) return;

    if (!worship.data.preacherMemberId) {
      setPreacherSelected(null);
      return;
    }
    if (worship.data.preacherMemberName) {
      setPreacherSelected({
        id: worship.data.preacherMemberId,
        label: worship.data.preacherMemberName,
      });
    }
  }, [deferSave, worship.data?.preacherMemberId, worship.data?.preacherMemberName]);

  const invalidateWorship = async () => {
    await queryClient.invalidateQueries({ queryKey: ['programs', 'occurrences'] });
  };

  useImperativeHandle(
    ref,
    () => ({
      applyDraft: async () => {
        if (!deferSave || planDates.length === 0) return;
        await applyWorshipDraft({
          programId,
          dates: planDates,
          draft: {
            preacherMemberId: draftPreacherId,
            includesCommunion: draftIncludesCommunion,
            assignments: draftAssignments.map((assignment) => ({
              memberId: assignment.memberId,
              roleKind: assignment.roleKind,
            })),
          },
        });
        await invalidateWorship();
      },
    }),
    [
      deferSave,
      draftAssignments,
      draftIncludesCommunion,
      draftPreacherId,
      planDates,
      programId,
      queryClient,
    ],
  );

  const updateWorship = useMutation({
    mutationFn: (body: { preacherMemberId?: string | null; includesCommunion?: boolean }) =>
      api.patch(`/programs/occurrences/${occurrenceId}/worship`, {
        ...body,
        ...worshipApplyPayload,
      }),
    onSuccess: async () => {
      toast.success(t('worship.saved'));
      await invalidateWorship();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const assign = useMutation({
    mutationFn: () =>
      api.post(`/programs/occurrences/${occurrenceId}/worship/assignments`, {
        memberId,
        roleKind,
        ...worshipApplyPayload,
      }),
    onSuccess: async () => {
      toast.success(t('worship.assignmentSaved'));
      setAssignOpen(false);
      setMemberId(null);
      setMemberSelected(null);
      setRoleKind('liturgist');
      await invalidateWorship();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const removeAssignment = useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete(`/programs/occurrences/${occurrenceId}/worship/assignments/${assignmentId}`, {
        body: worshipApplyPayload,
      }),
    onSuccess: async () => {
      toast.success(t('worship.assignmentRemoved'));
      await invalidateWorship();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const handlePreacherChange = (id: string | null) => {
    const option = preacherOptions.find((row) => row.id === id) ?? null;
    setPreacherSelected(option);
    if (deferSave) {
      setDraftPreacherId(id);
      return;
    }
    updateWorship.mutate({ preacherMemberId: id });
  };

  const handleCommunionChange = (next: boolean) => {
    if (deferSave) {
      setDraftIncludesCommunion(next);
      return;
    }
    updateWorship.mutate({ includesCommunion: next });
  };

  const handleAssignSubmit = () => {
    if (!memberId || !memberSelected) return;
    if (deferSave) {
      setDraftAssignments((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          memberId,
          memberName: memberSelected.label,
          roleKind,
        },
      ]);
      closeAssignDialog();
      return;
    }
    assign.mutate();
  };

  const handleRemoveAssignment = (assignmentId: string) => {
    if (deferSave) {
      setDraftAssignments((current) => current.filter((row) => row.id !== assignmentId));
      return;
    }
    removeAssignment.mutate(assignmentId);
  };

  const canPlan = deferSave ? planDates.length > 0 : Boolean(occurrenceId);
  const headerActions =
    canEdit && canPlan ? (
      <Button size="sm" onClick={() => setAssignOpen(true)}>
        {t('worship.addAssignment')}
      </Button>
    ) : null;

  const worshipFields = (
    <div className="space-y-4">
      <FormField label={t('worship.preacher')} hint={t('worship.preacherHint')}>
        {(field) => (
          <EntityCombobox
            id={field.id}
            value={preacherMemberId}
            onChange={handlePreacherChange}
            options={preacherOptions}
            selected={preacherSelected}
            onQueryChange={setPreacherQuery}
            loading={(ministers.isLoading && !ministers.data) || (members.isLoading && !members.data)}
            placeholder={t('worship.memberPlaceholder')}
            emptyMessage={t('worship.memberEmpty')}
            clearLabel={tCommon('action.clearSelection')}
            disabled={!canEdit || (!deferSave && updateWorship.isPending)}
            popoverPortal={embedded ? 'dialog' : 'body'}
          />
        )}
      </FormField>

      <label className="flex items-center gap-2 text-body-sm">
        <Switch
          checked={includesCommunion}
          disabled={!canEdit || (!deferSave && updateWorship.isPending)}
          onCheckedChange={handleCommunionChange}
        />
        {t('worship.includesCommunion')}
      </label>

      {visibleAssignments.length === 0 ? (
        <p className="text-body-sm text-fg-secondary">{t('worship.emptyAssignments')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {visibleAssignments.map((assignment) => (
            <li
              key={assignment.id}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-body-sm font-medium text-fg">{assignment.memberName}</p>
                <p className="text-caption text-fg-secondary">
                  {assignment.roleKind
                    ? t(`worship.roleKind.${assignment.roleKind}`)
                    : 'role' in assignment
                      ? assignment.role
                      : '—'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!deferSave && 'status' in assignment ? (
                  <Badge variant="neutral">
                    {t(`worship.assignmentStatus.${assignment.status}`, {
                      defaultValue: assignment.status,
                    })}
                  </Badge>
                ) : null}
                {canEdit ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemoveAssignment(assignment.id)}
                  >
                    {tCommon('action.remove')}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const body: ReactNode =
    planDates.length === 0 ? (
      <p className="text-body-sm text-fg-secondary">{t('worship.noDayDates')}</p>
    ) : (
      <div className="space-y-4">
        {planDates.length === 1 && primaryPlanDate ? (
          <p className="text-body-sm text-fg-secondary">
            {t('worship.forDate', { date: formatDate(primaryPlanDate) })}
          </p>
        ) : (
          <p className="text-caption text-fg-secondary">{t('worship.appliesToAllDays')}</p>
        )}
        {deferSave ? (
          <p className="text-caption text-fg-secondary">{t('worship.deferredSaveHint')}</p>
        ) : null}
        {(resolving || worship.isLoading || (deferSave && hydrateExisting && !draftHydrated)) ? (
          <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
        ) : (
          worshipFields
        )}
      </div>
    );

  const closeAssignDialog = () => {
    setAssignOpen(false);
    setMemberId(null);
    setMemberSelected(null);
    setRoleKind('liturgist');
  };

  const assignDialog = (
    <Dialog
      open={assignOpen}
      onOpenChange={(open) => {
        if (!open) closeAssignDialog();
        else setAssignOpen(true);
      }}
    >
      <DialogContent size="md" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('worship.addAssignment')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="overflow-visible">
          <div className="space-y-4">
            <FormField label={t('worship.roleLabel')} required>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={roleKind}
                  onChange={(value) => setRoleKind((value as RoleKind) ?? 'liturgist')}
                  options={ROLE_KINDS.map((value) => ({
                    value,
                    label: t(`worship.roleKind.${value}`),
                  }))}
                  popoverPortal="dialog"
                />
              )}
            </FormField>

            <FormField label={t('worship.member')} required>
              {(field) => (
                <EntityCombobox
                  id={field.id}
                  value={memberId}
                  onChange={(id) => {
                    setMemberId(id);
                    setMemberSelected(assignMemberOptions.find((row) => row.id === id) ?? null);
                  }}
                  options={assignMemberOptions}
                  selected={memberSelected}
                  onQueryChange={setMemberQuery}
                  loading={(ministers.isLoading && !ministers.data) || (members.isLoading && !members.data)}
                  placeholder={t('worship.memberPlaceholder')}
                  emptyMessage={t('worship.memberEmpty')}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={closeAssignDialog}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            type="button"
            loading={!deferSave && assign.isPending}
            disabled={!memberId}
            onClick={handleAssignSubmit}
          >
            {t(deferSave ? 'worship.addAssignment' : 'worship.saveAssignment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (embedded) {
    return (
      <div
        className={cn(
          sidePanel
            ? 'flex h-full min-h-0 flex-col rounded-lg border border-border bg-surface-secondary/40 p-4'
            : 'space-y-3',
          className,
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className={sidePanel ? 'text-card-title text-fg' : 'text-body-sm font-medium text-fg'}>
              {t('worship.title')}
            </p>
            <p className="text-caption text-fg-secondary">
              {sidePanel ? t('worship.sidePanelDescription') : t('worship.embeddedDescription')}
            </p>
          </div>
          {headerActions}
        </div>
        <div className={sidePanel ? 'mt-4 min-h-0 flex-1 space-y-4' : undefined}>{body}</div>
        {assignDialog}
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border border-border p-4', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-card-title text-fg">{t('worship.title')}</p>
          <p className="text-body-sm text-fg-secondary">{t('worship.description')}</p>
        </div>
        {headerActions}
      </div>
      {body}
      {assignDialog}
    </div>
  );
});
